/**
 * COM-R.155 — the client resolver reads one role system, the database reads both.
 *
 * ⚠️ THIS FILE DECIDES NOTHING THE DATABASE DOES NOT ALSO DECIDE.
 *
 * Same division as `commissionPermissions.ts`: every DEFINER gate re-checks in
 * the database through `cost_holds_permission()`, so what lives here only
 * decides whether a control is SHOWN. The failure this fixes is therefore a
 * hidden control, not an open door — which is the only reason a widening change
 * to authority resolution is safe to make at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WAS WRONG
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `PermissionContext.tsx` chose a role system with an if/else:
 *
 *     if (userData?.role_id) { ...legacy only... } else { ...user_roles... }
 *
 * so anybody holding a legacy `users.role_id` had their `user_roles` grants
 * ignored entirely. `cost_holds_permission()` does not work that way. Its own
 * comment says why — "556 of 574 users live only in the legacy one" — and it
 * reads the UNION:
 *
 *     rp.role_id = (SELECT u.role_id FROM users u WHERE u.id = auth.uid())
 *     OR rp.role_id IN (SELECT ur.role_id FROM user_roles ur WHERE ur.user_id = auth.uid())
 *
 * Measured against live on 2026-08-26, over 471 active users: 8465 grants
 * resolved before, 8722 after. **8 users gained 257 grants. Nobody lost
 * anything** — the losses query returned zero rows, and that is the number that
 * mattered, not the gains. See `rolePermissionResolution.test.ts` T09.
 *
 * ⚠️ THE OBVIOUS FIX SUBTRACTS, AND LOOKS LIKE IT WIDENS.
 *
 * The natural shape is "if any role from either system is Superadmin, return the
 * hard-coded Superadmin set" — a short-circuit, because that is what both
 * branches already did. It is wrong. Role 74 Agency Admin carries **53
 * permissions that the hard-coded list does not**, so short-circuiting on a
 * newly-discovered `user_roles` Superadmin row would have STRIPPED 53 grants
 * from `aglasier24@gmail.com` while the commit message claimed to widen. The
 * union here is additive by construction and T09 pins it.
 *
 * ⚠️ THE HARD-CODED SUPERADMIN LIST IS GONE, DELIBERATELY.
 *
 * `PermissionContext` hard-coded 17 permissions for `role_id === 42`. Role 42
 * actually carries **76** rows in `role_permissions`, and the 17 are a strict
 * subset of the 76 — measured, zero of the 17 absent from the table. So the
 * list was pure loss: four Superadmins were shown 17 of 76. Reading the table
 * for every role id, role 42 included, subsumes it. Verified before removing it
 * that exactly one role is named Superadmin (id 42) and that it carries 76
 * grants, so no identity depended on the constant.
 *
 * ⚠️ THE ACCOUNT RULE IS THE DATABASE'S, AND IT CHANGED IN RBAC-7.1.
 *
 * Until 2026-11-03 `cost_holds_permission()` was tenant-blind, so this module
 * was too. `holds_permission_for()` (20261103a3) now decides, and
 * `heldUserRoleIds()` / `legacyRoleCounts()` below mirror it: a role counts in
 * an account only when global or owned by it, a `user_roles` row only in its
 * own account, the global Superadmin excepted. Measured before applying, over
 * 639 users: no active person lost a grant in their own account. Change one
 * side and `verify_rbac_7_1_platform_authority.sql` B4/B6/B7 plus T13-T20 are
 * the pair that must move together.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS STILL DIVERGENT, AND IS NOT FIXED HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `cost_holds_permission()` matches `pm.name IN (p_permission, 'system.override')`,
 * so holding `system.override` satisfies EVERY permission name in the database.
 * `hasPermission()` is literal set membership and does not imply anything.
 *
 * That gap is real and it is measurable: there are 81 distinct permissions and
 * role 42 grants 76. The five it does not grant are exactly the five commission
 * money permissions — `commission.view`, `.approve`, `.pay`, `.export`,
 * `.adjust`. So a Superadmin whose only role is 42 resolves without them here
 * while the database answers true.
 *
 * It is not fixed in this module because `commissionPermissions.ts` already
 * carries that implication for those five, on purpose, with its own reasoning
 * and its own tests. Widening `hasPermission` itself would alter every one of
 * the 110 files that call it. T10 pins the current behaviour so that it stays a
 * decision rather than becoming an accident.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Inputs, shaped as the rows actually arrive
// ─────────────────────────────────────────────────────────────────────────────

/** One `role_permissions` row joined to `permissions.name`. */
export interface RoleGrant {
  role_id: number;
  permission: string;
}

export interface RoleResolutionInput {
  /** `users.role_id`. Null for the identities that never had one. */
  legacyRoleId?: number | null;
  /**
   * `user_roles.role_id` for this user that count in the current account.
   * Filter them with `heldUserRoleIds()` first (RBAC-7.1); this function only
   * unions what it is given.
   */
  userRoleIds?: readonly (number | null | undefined)[] | null;
  /** Rows for the resolved role ids. Extra rows for other roles are ignored. */
  grants?: readonly RoleGrant[] | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// The union
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every role id this identity holds, from both systems, de-duplicated.
 *
 * Order is legacy-first then `user_roles` in the order given, so the result is
 * stable and a test can assert on it. Nulls are dropped rather than coerced:
 * `role_id` is nullable and `0` is not a role.
 */
export function resolveRoleIds(input: RoleResolutionInput): number[] {
  const seen = new Set<number>();
  const out: number[] = [];

  const take = (id: number | null | undefined) => {
    if (typeof id !== 'number' || !Number.isFinite(id)) return;
    if (seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };

  take(input.legacyRoleId);
  for (const id of input.userRoleIds ?? []) take(id);

  return out;
}

/**
 * The permission names this identity resolves to.
 *
 * Additive by construction: the result is a union over every held role, so no
 * role can remove what another granted. That property is the whole safety
 * argument for this change and T09 asserts it directly.
 */
export function resolvePermissions(input: RoleResolutionInput): Set<string> {
  const roleIds = new Set(resolveRoleIds(input));
  const permissions = new Set<string>();

  if (roleIds.size === 0) return permissions;

  for (const grant of input.grants ?? []) {
    if (!grant || typeof grant.permission !== 'string' || grant.permission === '') continue;
    if (!roleIds.has(grant.role_id)) continue;
    permissions.add(grant.permission);
  }

  return permissions;
}

// ─────────────────────────────────────────────────────────────────────────────
// RBAC-7.1 · the account rule, mirrored from holds_permission_for()
// ─────────────────────────────────────────────────────────────────────────────

/** A `roles` row as PostgREST embeds it. */
export interface EmbeddedRole {
  id?: number | null;
  name?: string | null;
  global?: boolean | null;
  tenant_id?: string | null;
}

/** One `user_roles` row with its role embedded (object or one-element array). */
export interface UserRoleRow {
  role_id: number | null;
  tenant_id: string | null;
  roles?: EmbeddedRole | EmbeddedRole[] | null;
}

const embedded = (r: UserRoleRow['roles']): EmbeddedRole | null =>
  (Array.isArray(r) ? r[0] : r) ?? null;

/** A role counts in an account only when it is global or owned by that account (NFR-5c). */
const roleCountsIn = (role: EmbeddedRole | null, tenantId: string): boolean =>
  !!role && (role.global === true || (role.tenant_id ?? null) === tenantId);

/**
 * Whether `users.role_id` counts in `currentTenantId`. It has no tenant column:
 * a global legacy role applies wherever the person is admitted, a non-global one
 * only in the account that owns it.
 */
export function legacyRoleCounts(
  role: EmbeddedRole | null | undefined,
  currentTenantId: string | null | undefined,
): boolean {
  if (!currentTenantId) return false;
  return roleCountsIn(role ?? null, currentTenantId);
}

/**
 * The `user_roles` role ids that count in `currentTenantId`: the row is in that
 * account and the role is global or owned by it, or the role is the global
 * Superadmin, which counts everywhere (as `is_superadmin()` treats it).
 *
 * No current account resolves to nothing, never to every row.
 */
export function heldUserRoleIds(
  rows: readonly UserRoleRow[] | null | undefined,
  currentTenantId: string | null | undefined,
): number[] {
  if (!currentTenantId) return [];
  const out: number[] = [];
  for (const row of rows ?? []) {
    if (!row || typeof row.role_id !== 'number') continue;
    const role = embedded(row.roles);
    const superadmin = role?.global === true && role?.name === 'Superadmin';
    if (superadmin || (row.tenant_id === currentTenantId && roleCountsIn(role, currentTenantId))) {
      out.push(row.role_id);
    }
  }
  return out;
}
